import {
  Subject,
  type GenericSubject,
} from "@twinkltech/curriculum-architect-domain-hooks";
import {
  Button,
  IconActionsOpenInNew,
  IconAlertsCheckCircle,
  Text,
} from "@twinkltech/web-ui";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { SubjectHelper } from "@/utils/SubjectHelper/SubjectHelper";

import { COMMON_NS } from "@/i18n/common/common.i18n";

import { StyledTag } from "../StyledTag/StyledTag";

import { SUBJECT_CARD_NS } from "./SubjectCard.i18n";

export const SubjectCard = ({
  subject,
  variant = "default",
  ...props
}: {
  subject: Subject | GenericSubject;
  curriculumId?: string;
  variant?: "default" | "add-on" | "in-development";
} & React.ComponentProps<typeof Link>) => {
  const { t } = useTranslation(SUBJECT_CARD_NS);
  const { imageUrl } = SubjectHelper.getCardDecorations(subject.slug);

  return (
    <Link
      className="group rounded-lg flex border-neutral-150 border flex-row flex-grow hover:bg-neutral-100 focus-within:bg-neutral-100 focus-within:border-neutral-100"
      {...props}
    >
      <div
        className={[
          "w-[96px] min-w-[96px] h-[80px] px-5 py-4 rounded-l-lg",
          ["default", "add-on"].includes(variant) &&
            "bg-blue-50 border-r-blue-500 group-hover:bg-blue-150 group-focus-within:bg-blue-150",
          variant === "in-development" &&
            "bg-neutral-100 group-hover:bg-neutral-100 group-focus-within:bg-neutral-100",
          imageUrl ? "flex items-center justify-center" : "hidden",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <img
          alt=""
          // if the version is the in-development version, grayscale and fade
          className={`object-cover max-h-[48px] max-w-[48px] ${
            variant === "in-development"
              ? "grayscale opacity-70 group-hover:opacity-100 group-focus-within:opacity-100"
              : ""
          }`}
          role="presentation"
          src={imageUrl}
        />
      </div>
      <div
        className={[
          "grow-1 py-2 flex justify-between items-center pl-4 pr-6 border-l-8",
          variant !== "in-development" && " border-l-blue-500",
          variant === "in-development" && "border-l-neutral-300",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="flex flex-row justify-between items-center gap-6">
          <div className="flex gap-2 items-center">
            {variant === "default" && (
              <IconAlertsCheckCircle className="min-w-6 text-green-500" />
            )}
            <Text as="h2" className="line-clamp-1" variant="xl-medium">
              {subject.label}
            </Text>
          </div>

          {variant === "default" && (
            <StyledTag
              size="sm"
              variant={
                subject.status === "not_yet_planned"
                  ? "default"
                  : subject.status === "planned"
                  ? "green"
                  : "light-blue-alt"
              }
            >
              {t(`statuses.${subject.status}`, { ns: COMMON_NS })}
            </StyledTag>
          )}

          {variant === "add-on" && (
            <div className="flex gap-2">
              {SubjectHelper.getSubjectAddOnTags(subject.slug).map((tag) => (
                <StyledTag key={tag.label} size="sm" variant={tag.variant}>
                  <span>{t(`tags.${tag.label}`)}</span>
                </StyledTag>
              ))}
            </div>
          )}
          {variant === "in-development" && (
            <StyledTag size="sm" variant="default-alt">
              {t("in-development.tag")}
            </StyledTag>
          )}
        </div>
        <div className="ml-2">
          {variant === "default" && (
            <Button
              className="group-hover:bg-[#155C95] hover:border-initial"
              size="sm"
              tabIndex={-1}
              variant="secondary"
            >
              {t("cta.view-and-plan")}
            </Button>
          )}
          {variant === "add-on" && (
            <Button
              className="hover:border-initial"
              size="sm"
              tabIndex={-1}
              variant="outlined"
            >
              {t("cta.explore-find-out-more")}
            </Button>
          )}
          {variant === "in-development" && (
            <Button
              className="hover:border-initial"
              size="sm"
              tabIndex={-1}
              variant="outlined"
            >
              {t("cta.find-out-more")}
              <IconActionsOpenInNew />
            </Button>
          )}
        </div>
      </div>
    </Link>
  );
};
